import { parseMaterialHref } from '@shared/material'
import { Extension } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'
import { useCallback } from 'react'
import { useFocusMention } from '@/session/focus'
import { parseMention } from '@/session/mentions'
import { useBoardCtx } from './context'

// Links in a card that point into moot (item://, edge://, region://, mat://)
// act like the chips in the chat: a click shows the thing on the board, or
// opens the reader at that place, instead of following the link.

export function isBoardHref(href: string): boolean {
  return /^(item|edge|region|mat):/.test(href)
}

/** What a click on a board link does: show it on the board, or open the material there. */
export function useOpenBoardLink() {
  const focus = useFocusMention()
  const { reader } = useBoardCtx()
  return useCallback(
    (href: string) => {
      const m = parseMention(href)
      if (m) {
        void focus(m)
        return
      }
      const place = parseMaterialHref(href)
      if (place) reader.open(place.materialId, place.page, place.rect)
    },
    [focus, reader],
  )
}

/** The board link under a click, if any. */
export function boardHrefAt(target: EventTarget | null): string | null {
  const a = target instanceof Element ? target.closest('a[href]') : null
  const href = a?.getAttribute('href') ?? ''
  return isBoardHref(href) ? href : null
}

/** Fired on the editor's element when a board link in it is clicked; `detail` is the href. */
export const BOARD_LINK_EVENT = 'moot-board-link'

/**
 * For the card editor: a click on a board link is announced as
 * BOARD_LINK_EVENT instead of placing the caret, for the card to act on.
 */
export const BoardLinkClicks = Extension.create({
  name: 'boardLinks',
  // Ahead of the link mark's own click handling.
  priority: 1001,
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handleClick(view, _pos, event) {
            const href = boardHrefAt(event.target)
            if (!href) return false
            event.preventDefault()
            view.dom.dispatchEvent(new CustomEvent(BOARD_LINK_EVENT, { detail: href }))
            return true
          },
        },
      }),
    ]
  },
})
